import { ID, Reminder, ReminderBasedOn } from '@griply/types';

type ReminderTiming = Pick<Reminder, 'offset' | 'basedOn'>;

export const isSameReminder = (a: ReminderTiming, b: ReminderTiming): boolean =>
  a.offset === b.offset && a.basedOn === b.basedOn;

export const removeReminder = (
  reminders: Reminder[],
  target: ReminderTiming,
): Reminder[] =>
  reminders.filter((reminder) => !isSameReminder(reminder, target));

// An explicit "add" flow must never remove: re-picking an existing timing is a no-op, not a toggle.
export const addReminder = (
  reminders: Reminder[],
  reminder: Reminder,
): Reminder[] =>
  reminders.some((existing) => isSameReminder(existing, reminder))
    ? reminders
    : [...reminders, reminder];

export const toggleReminder = (
  reminders: Reminder[],
  reminder: Reminder,
): Reminder[] =>
  reminders.some((existing) => isSameReminder(existing, reminder))
    ? removeReminder(reminders, reminder)
    : [...reminders, reminder];

const BASED_ON_RANK: Record<ReminderBasedOn, number> = {
  [ReminderBasedOn.DayStart]: 0,
  [ReminderBasedOn.Start]: 1,
};

// Insertion order differs per app for the same picks, so reading order is derived, never stored.
export const sortReminders = (reminders: Reminder[]): Reminder[] =>
  reminders
    .slice()
    .sort(
      (a, b) =>
        BASED_ON_RANK[a.basedOn] - BASED_ON_RANK[b.basedOn] ||
        a.offset - b.offset ||
        (a.id ?? '').localeCompare(b.id ?? ''),
    );

// The settings default and legacy rows arrive id-less; rows must carry ids before they are stored or addressed.
export const ensureReminderIds = (
  reminders: Reminder[],
  createId: () => ID,
): Reminder[] =>
  reminders.map((reminder) =>
    reminder.id ? reminder : { ...reminder, id: createId() },
  );

// A relative reminder resolves against the item's start time, so without one it never fires.
export const isReminderUnresolvable = (
  reminder: Reminder,
  hasTime: boolean,
): boolean => reminder.basedOn === ReminderBasedOn.Start && !hasTime;

export const getDefaultReminderBasedOn = (
  reminders: Reminder[] | null | undefined,
  hasTime: boolean,
): ReminderBasedOn => {
  const active = reminders ?? [];

  if (active.some(({ basedOn }) => basedOn === ReminderBasedOn.Start)) {
    return ReminderBasedOn.Start;
  }

  if (active.some(({ basedOn }) => basedOn === ReminderBasedOn.DayStart)) {
    return ReminderBasedOn.DayStart;
  }

  return hasTime ? ReminderBasedOn.Start : ReminderBasedOn.DayStart;
};

// An empty array means "explicitly no reminders" — only a missing array falls back to the legacy value.
export const shouldShowLegacyReminder = (
  reminders: Reminder[] | null | undefined,
  reminderTime: number | null | undefined,
): reminderTime is number => reminders == null && reminderTime != null;

// A legacy time-of-day fires at that clock time on the task's day — a DayStart reminder with the same offset.
export const legacyReminderTimeToReminder = (
  reminderTime: number,
  id?: ID | null,
): Reminder => ({
  id: id ?? null,
  offset: reminderTime,
  basedOn: ReminderBasedOn.DayStart,
});

export const resolveTaskReminders = (
  reminders: Reminder[] | null | undefined,
  reminderTime: number | null | undefined,
): Reminder[] | null => {
  if (shouldShowLegacyReminder(reminders, reminderTime)) {
    return [legacyReminderTimeToReminder(reminderTime)];
  }
  return reminders ?? null;
};
